import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import { footerConfigs } from '~/company/configs/footerConfig';
import type { IconTypes } from '~/components/Icon/IconTypes';
import { useGlobal } from '~/hooks/useGlobal';

import Icon from '../Icon/Icon';

import styles from './LayoutLocaleeV2.module.scss';
import { PaymentMethodsList } from './PaymentMethodsList';

export const LayoutLocaleeV2 = observer(
  React.forwardRef<
    HTMLDivElement,
    Omit<React.ComponentPropsWithRef<'footer'>, 'children'>
  >((props, ref) => {
    const { t } = useTranslation();
    const { isMobile } = useGlobal();

    const footerConfig = footerConfigs[company.name] || footerConfigs.default;
    const links = [
      {
        link: company.config.links.linkedin,
        icon: 'linkedin',
      },
      {
        link: company.config.links.instagram,
        icon: 'insta',
      },
      {
        link: company.config.links.facebook,
        icon: 'facebook',
      },
      {
        link: company.config.links.tiktok,
        icon: 'tiktok',
      },
      {
        link: company.config.links.youtube,
        icon: 'youtube',
      },
      {
        link: company.config.links.twitter,
        icon: 'twitter',
      },
      {
        link: company.config.links.whatsapp,
        icon: 'whatsapp',
      },
    ].filter((item) => item.link);
    const hasAppLinks =
      company.config.appStoreUrl.android ||
      company.config.appStoreUrl.ios ||
      company.images.qr.maxi;

    if (!footerConfig) {
      return null;
    }

    return (
      <footer
        {...props}
        ref={ref}
        className={styles.footer}
        data-company={company.name}
      >
        <div className={styles.container}>
          <div className={styles.topSection}>
            {/* Hotline Section */}
            <div className={styles.hotlineSection}>
              <div className={styles.hotlineContent}>
                <div className={styles.sectionTitle}>{t('hotline')}</div>
                <div className={styles.phoneNumber}>
                  {company.config.phoneNumber}
                </div>
              </div>
              {/* TODO: Add contact support to config */}
              {company.hasContactSupport && company.config.links.whatsapp && (
                <a
                  href={company.config.links.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.contactBtn}
                >
                  <Icon type="whatsapp" size={16} />
                  {t('contactSupport')}
                </a>
              )}
              {company.config.contactEmail && (
                <a
                  href={`mailto:${company.config.contactEmail}`}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.contactBtn}
                >
                  {company.config.contactEmail}
                </a>
              )}
              {company.hasAddressInFooter && (
                <div className={styles.hotlineContent}>
                  <div className={styles.sectionTitle}>
                    {company.config.address}
                  </div>
                </div>
              )}
              {company.showWorkingHoursInFooter && (
                <div className={styles.hotlineContent}>
                  <div className={styles.sectionTitle}>
                    {t('workingHours', {
                      start: company.config.workingHours?.start,
                      end: company.config.workingHours?.end,
                    })}
                    &nbsp;
                    {t('allDays')}
                  </div>
                </div>
              )}
            </div>

            {/* Legal Section */}
            {footerConfig.sections.map((section) => (
              <div key={section.title} className={styles.legalSection}>
                <div className={styles.sectionTitle}>{t(section.title)}</div>
                <div className={styles.links}>
                  {section.items.map((item) => (
                    <Link
                      key={item.link}
                      to={item.link}
                      data-link={item.dataLink}
                      className={styles.link}
                    >
                      {t(item.title)}
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            {/* Payment Section */}
            <div className={styles.paymentSection}>
              <PaymentMethodsList classNames={styles.paymentMethodsList} />
            </div>

            {/* App Download Section */}
            {hasAppLinks && (
              <div className={styles.appDownloadSection}>
                {company.images.qr.maxi && (
                  <img
                    className={styles.qrCode}
                    src={company.images.qr.maxi}
                    alt="QR"
                  />
                )}
                {(company.config.appStoreUrl.android ||
                  company.config.appStoreUrl.ios) && (
                  <div className={styles.appDownloadContent}>
                    <div className={styles.title}>
                      {isMobile
                        ? t('downloadOurApp')
                        : t('downloadTheAppForYourPhone')}
                    </div>
                    <div className={styles.storeButtons}>
                      {company.config.appStoreUrl.ios && (
                        <a
                          href={company.config.appStoreUrl.ios}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.storeButton}
                        >
                          <Icon type="apple" size={isMobile ? 28 : 26} />
                          <div className={styles.storeName}>
                            <span className={styles.storeNameTitle}>
                              {t('downloadOn')}
                            </span>
                            <span className={styles.storeNameText}>
                              App Store
                            </span>
                          </div>
                        </a>
                      )}
                      {company.config.appStoreUrl.android && (
                        <a
                          href={company.config.appStoreUrl.android}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.storeButton}
                        >
                          <Icon type="gplay" size={isMobile ? 28 : 26} />
                          <div className={styles.storeName}>
                            <span className={styles.storeNameTitle}>
                              {t('getOn')}
                            </span>
                            <span className={styles.storeNameText}>
                              Google Play
                            </span>
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bottom Section */}
          <div className={styles.bottomSection}>
            <div className={styles.logoSection}>
              <img
                src={company.images.logo.light}
                alt="logo"
                className={styles.logo}
              />
              <span className={styles.copyright}>
                © {new Date().getFullYear()} {company.config.officialName} All
                rights reserved.
              </span>
            </div>

            {links.length > 0 && (
              <div className={styles.socials}>
                {links.map((item) => (
                  <a
                    key={item.link}
                    href={item.link}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.socialButton}
                  >
                    <Icon type={item.icon as IconTypes} size={20} />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </footer>
    );
  }),
);
